import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ChangePassword = () => {
  const [formData, setFormData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState({
    old: false,
    new: false,
    confirm: false,
  });
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Validasi
    if (!formData.old_password || !formData.new_password || !formData.confirm_password) {
      setError('Semua field harus diisi');
      setLoading(false);
      return;
    }

    if (formData.new_password.length < 6) {
      setError('Password baru minimal 6 karakter');
      setLoading(false);
      return;
    }

    if (formData.new_password !== formData.confirm_password) {
      setError('Password baru tidak cocok');
      setLoading(false);
      return;
    }

    if (formData.old_password === formData.new_password) {
      setError('Password baru tidak boleh sama dengan password lama');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          old_password: formData.old_password,
          new_password: formData.new_password,
        }),
        signal: AbortSignal.timeout(15000),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Gagal mengubah password');
        console.error('Change password error:', response.status, data);
        return;
      }

      setSuccess('✓ Password berhasil diubah! Silakan login kembali dengan password baru.');
      setFormData({
        old_password: '',
        new_password: '',
        confirm_password: '',
      });

      // Redirect ke login setelah 2 detik
      setTimeout(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      }, 2000);
    } catch (err) {
      if (err.name === 'AbortError') {
        setError('Request timeout. Coba lagi atau hubungi admin.');
      } else if (err instanceof TypeError && err.message.includes('fetch')) {
        setError('Koneksi ke server gagal. Periksa internet connection Anda.');
      } else {
        setError('Error: ' + err.message);
      }
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard')}
          className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
        >
          ← Kembali ke Dashboard
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-3xl font-bold mb-2">Ubah Password</h1>
        <p className="text-gray-600 mb-6">Masukkan password lama Anda dan password baru</p>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Old Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password Lama
            </label>
            <div className="relative">
              <input
                type={showPassword.old ? 'text' : 'password'}
                name="old_password"
                value={formData.old_password}
                onChange={handleChange}
                className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Masukkan password lama"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() =>
                  setShowPassword({ ...showPassword, old: !showPassword.old })
                }
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-900 focus:outline-none text-xl pointer-events-auto z-10"
                title={showPassword.old ? 'Sembunyikan password' : 'Tampilkan password'}
              >
                {showPassword.old ? '🔓' : '🔒'}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password Baru
            </label>
            <div className="relative">
              <input
                type={showPassword.new ? 'text' : 'password'}
                name="new_password"
                value={formData.new_password}
                onChange={handleChange}
                className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Masukkan password baru (minimal 6 karakter)"
                minLength="6"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() =>
                  setShowPassword({ ...showPassword, new: !showPassword.new })
                }
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-900 focus:outline-none text-xl pointer-events-auto z-10"
                title={showPassword.new ? 'Sembunyikan password' : 'Tampilkan password'}
              >
                {showPassword.new ? '🔓' : '🔒'}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Konfirmasi Password Baru
            </label>
            <div className="relative">
              <input
                type={showPassword.confirm ? 'text' : 'password'}
                name="confirm_password"
                value={formData.confirm_password}
                onChange={handleChange}
                className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Konfirmasi password baru"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() =>
                  setShowPassword({ ...showPassword, confirm: !showPassword.confirm })
                }
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-900 focus:outline-none text-xl pointer-events-auto z-10"
                title={showPassword.confirm ? 'Sembunyikan password' : 'Tampilkan password'}
              >
                {showPassword.confirm ? '🔓' : '🔒'}
              </button>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-4 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 font-semibold disabled:bg-gray-400"
            >
              {loading ? 'Memproses...' : 'Ubah Password'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              disabled={loading}
              className="flex-1 bg-gray-600 text-white py-2 rounded-lg hover:bg-gray-700 font-semibold disabled:bg-gray-400"
            >
              Batal
            </button>
          </div>
        </form>

        <div className="mt-8 p-4 bg-blue-50 border-l-4 border-blue-400 rounded">
          <p className="text-sm text-blue-800">
            <strong>💡 Tips Keamanan:</strong>
          </p>
          <ul className="text-sm text-blue-700 mt-2 space-y-1 ml-4">
            <li>✓ Gunakan kombinasi huruf besar, kecil, dan angka</li>
            <li>✓ Jangan gunakan password yang mudah ditebak</li>
            <li>✓ Gunakan password yang unik untuk setiap akun</li>
            <li>✓ Ingat password Anda dengan aman</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
